import { useState } from 'react';
import openapiSpec from '../generated/openapi.json';
import './ApiEndpoints.css';

// A small Swagger-style endpoint browser. Reads the OpenAPI spec bundled by
// `make api-ref` and renders every operation for a given tag: a colored method
// row that expands to show parameters, request body, and responses.
//
// Mounted from generated resource pages via the `<api-endpoints tag="...">`
// element, mapped to this component in ArticleView.

type JsonSchema = {
  type?: string;
  properties?: Record<string, JsonSchema>;
  required?: string[];
  items?: JsonSchema;
  enum?: unknown[];
  anyOf?: JsonSchema[];
  oneOf?: JsonSchema[];
  format?: string;
  description?: string;
};

type Operation = {
  summary?: string;
  description?: string;
  deprecated?: boolean;
  parameters?: Array<{
    name: string;
    in: string;
    required?: boolean;
    description?: string;
    schema?: JsonSchema;
  }>;
  requestBody?: { content?: Record<string, { schema?: JsonSchema }> };
  responses?: Record<string, { description?: string; content?: Record<string, { schema?: JsonSchema }> }>;
  tags?: string[];
  ['x-auth']?: string;
  ['x-scopes']?: string[];
};

type Spec = { paths: Record<string, Record<string, Operation>> };

const SPEC = openapiSpec as unknown as Spec;
const METHOD_ORDER: Record<string, number> = { GET: 0, POST: 1, PUT: 2, PATCH: 3, DELETE: 4 };
const AUTH_LABEL: Record<string, string> = {
  none: 'No auth',
  bearer: 'Token',
  token: 'Token',
  session: 'Session',
  admin: 'Admin',
};

type FlatOp = Operation & { method: string; path: string };

function operationsForTag(tag: string): FlatOp[] {
  const ops: FlatOp[] = [];
  for (const [pathName, item] of Object.entries(SPEC.paths)) {
    for (const [method, op] of Object.entries(item)) {
      if ((op.tags ?? []).includes(tag)) {
        ops.push({ ...op, method: method.toUpperCase(), path: pathName });
      }
    }
  }
  return ops.sort((a, b) =>
    a.path !== b.path
      ? a.path.localeCompare(b.path)
      : (METHOD_ORDER[a.method] ?? 9) - (METHOD_ORDER[b.method] ?? 9),
  );
}

/** A compact human label for a JSON-schema node. */
function typeLabel(schema?: JsonSchema): string {
  if (!schema) return 'any';
  if (schema.anyOf || schema.oneOf) {
    return (schema.anyOf ?? schema.oneOf ?? []).map(typeLabel).join(' | ');
  }
  if (schema.enum) return schema.enum.map(v => JSON.stringify(v)).join(' | ');
  if (schema.type === 'array') return `${typeLabel(schema.items)}[]`;
  if (schema.format) return `${schema.type} (${schema.format})`;
  return schema.type ?? 'object';
}

function objectShape(schema?: JsonSchema): { props: Record<string, JsonSchema>; required: Set<string> } | null {
  if (!schema) return null;
  if (schema.type === 'object' && schema.properties) {
    return { props: schema.properties, required: new Set(schema.required ?? []) };
  }
  // Arrays of objects: show the element shape, which is what callers care about.
  if (schema.type === 'array' && schema.items?.type === 'object' && schema.items.properties) {
    return { props: schema.items.properties, required: new Set(schema.items.required ?? []) };
  }
  return null;
}

function SchemaView({ schema }: { schema?: JsonSchema }) {
  if (!schema) return null;
  const shape = objectShape(schema);
  if (!shape) {
    return <pre className="api-schema-json">{JSON.stringify(schema, null, 2)}</pre>;
  }
  return (
    <div className="api-schema">
      {schema.type === 'array' && <div className="api-schema-note">array of:</div>}
      <table className="api-fields">
        <thead>
          <tr>
            <th>Field</th>
            <th>Type</th>
            <th></th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(shape.props).map(([name, prop]) => (
            <tr key={name}>
              <td className="api-field-name">{name}</td>
              <td className="api-field-type">{typeLabel(prop)}</td>
              <td>{shape.required.has(name) && <span className="api-req">required</span>}</td>
              <td className="api-field-desc">{prop.description ?? ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <details className="api-schema-raw">
        <summary>Raw schema</summary>
        <pre className="api-schema-json">{JSON.stringify(schema, null, 2)}</pre>
      </details>
    </div>
  );
}

function bodySchema(content?: Record<string, { schema?: JsonSchema }>): JsonSchema | undefined {
  return content?.['application/json']?.schema;
}

function OperationRow({ op }: { op: FlatOp }) {
  const [open, setOpen] = useState(false);
  const method = op.method.toLowerCase();
  const reqBody = bodySchema(op.requestBody?.content);
  const scopes = op['x-scopes'] ?? [];

  return (
    <div className={`api-op${op.deprecated ? ' api-op-deprecated' : ''}`}>
      <button
        type="button"
        className="api-op-head"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
      >
        <span className={`api-method api-method-${method}`}>{op.method}</span>
        <code className="api-op-path">{op.path}</code>
        <span className="api-op-summary">{op.summary}</span>
        <i className={`fa-solid fa-chevron-${open ? 'down' : 'right'} api-op-caret`}></i>
      </button>

      {open && (
        <div className="api-op-body">
          <div className="api-op-meta">
            {op['x-auth'] && (
              <span className="api-badge">{AUTH_LABEL[op['x-auth']] ?? op['x-auth']}</span>
            )}
            {scopes.map(s => (
              <span key={s} className="api-badge api-badge-scope">{s}</span>
            ))}
            {op.deprecated && <span className="api-badge api-badge-deprecated">deprecated</span>}
          </div>

          {op.description && <p className="api-op-desc">{op.description}</p>}

          {op.parameters && op.parameters.length > 0 && (
            <section>
              <h4>Parameters</h4>
              <table className="api-fields">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>In</th>
                    <th>Type</th>
                    <th></th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {op.parameters.map(p => (
                    <tr key={`${p.in}-${p.name}`}>
                      <td className="api-field-name">{p.name}</td>
                      <td>{p.in}</td>
                      <td className="api-field-type">{typeLabel(p.schema)}</td>
                      <td>{p.required && <span className="api-req">required</span>}</td>
                      <td className="api-field-desc">{p.description ?? ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {reqBody && (
            <section>
              <h4>Request body</h4>
              <SchemaView schema={reqBody} />
            </section>
          )}

          {op.responses && (
            <section>
              <h4>Responses</h4>
              {Object.entries(op.responses)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([status, res]) => {
                  const schema = bodySchema(res.content);
                  return (
                    <div key={status} className="api-response">
                      <div className="api-response-head">
                        <span className={`api-status api-status-${status[0]}xx`}>{status}</span>
                        <span>{res.description}</span>
                      </div>
                      {schema && <SchemaView schema={schema} />}
                    </div>
                  );
                })}
            </section>
          )}
        </div>
      )}
    </div>
  );
}

type Props = {
  tag?: string;
  node?: { properties?: { tag?: string } };
};

export function ApiEndpoints({ tag, node }: Props) {
  const resolved = tag ?? node?.properties?.tag;
  if (!resolved) return null;
  const ops = operationsForTag(resolved);
  if (ops.length === 0) {
    return <div className="api-endpoints-empty">No published endpoints for this resource.</div>;
  }
  return (
    <div className="api-endpoints">
      {ops.map(op => (
        <OperationRow key={`${op.method} ${op.path}`} op={op} />
      ))}
    </div>
  );
}
